import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router';

import { AppContentWrapper } from 'app/components/app-content-wrapper/app-content-wrapper';
import { Private } from 'app/components/router/private';
import { LoadingScreen } from 'app/components/ui/loading-screen';
import { ERROR_EVENT_NOT_FOUND } from 'app/shared/consts/error-codes';
import { ROUTES } from 'app/shared/consts/paths';

// One chunk per page so the initial bundle only loads the route the host
// iframe deep-linked to. Real bodies land in SMB-661 / 662 / 663.
const EventDetailPage = lazy(() =>
  import('app/pages/event-detail').then((m) => ({ default: m.EventDetailPage })),
);
const EventTimeSlotsPage = lazy(() =>
  import('app/pages/event-time-slots').then((m) => ({ default: m.EventTimeSlotsPage })),
);
const EventTimeSlotDetailsPage = lazy(() =>
  import('app/pages/event-time-slot-details').then((m) => ({ default: m.EventTimeSlotDetailsPage })),
);
const ErrorPage = lazy(() =>
  import('app/pages/error').then((m) => ({ default: m.ErrorPage })),
);
const DeeplinkPage = lazy(() =>
  import('app/pages/deeplink').then((m) => ({ default: m.DeeplinkPage })),
);

const guarded = (page: JSX.Element): JSX.Element => <Private>{page}</Private>;

/**
 * Catch-all for paths the host iframe deep-linked to but we don't serve. In
 * an events miniapp an unknown route is almost always a stale/garbled event
 * link, so redirect to /error carrying the dedicated `event_not_found` copy —
 * otherwise the empty generic-error channel falls back to the generic
 * "Something went wrong" (SMB-1001).
 *
 * The copy travels via navigation state, NOT the `genericErrors` channel, on
 * purpose: it's scoped to this single redirect. A channel entry would linger
 * for the session and — since <ErrorNavigator> redirects whenever the channel
 * is non-empty — bounce the user back to /error the moment they returned to a
 * valid page (e.g. host/OS back). Nav state leaves nothing behind. `replace`
 * keeps the stale URL out of history.
 */
const UnknownRouteFallback = (): JSX.Element => (
  <Navigate to={ROUTES.ERROR} replace state={{ errorCode: ERROR_EVENT_NOT_FOUND }} />
);

/**
 * Each service screen registers under both prefix variants
 * (`/providers/...` and `/building/:buildingUuid/providers/...`) — see
 * `ROUTES` in `shared/consts/paths.ts` for the full list.
 */
const renderRoutes = (paths: readonly string[], element: JSX.Element): JSX.Element[] =>
  paths.map((path) => <Route key={path} path={path} element={element} />);

export const AppRouter = (): JSX.Element => (
  <AppContentWrapper>
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {renderRoutes(ROUTES.EVENT_DETAIL, guarded(<EventDetailPage />))}
        {renderRoutes(ROUTES.EVENT_TIME_SLOTS, guarded(<EventTimeSlotsPage />))}
        {renderRoutes(ROUTES.EVENT_TIME_SLOT_DETAILS, guarded(<EventTimeSlotDetailsPage />))}
        <Route path={ROUTES.DEEPLINK} element={guarded(<DeeplinkPage />)} />
        <Route path={ROUTES.ERROR} element={<ErrorPage />} />
        <Route path="*" element={<UnknownRouteFallback />} />
      </Routes>
    </Suspense>
  </AppContentWrapper>
);
